import {useQuery} from 'react-query'
import {QUERIES, toAbsoluteUrl} from '../../../_metronic/helpers'
import {getSaleById, getSaleByPaymentLink} from './core/_request'
import axios, {AxiosError} from 'axios'
import {useParams} from 'react-router-dom'
import {HasErrorInShowPage} from '../../../components/HasErrorInShowPage'
import {NotFoundIdShowPage} from '../../../components/NotFoundIdShowPage'
import {Spinner} from '../../../components/Spinner'
import {formatMask, formatMonetaryToBr} from '../../../utils/formatMonetaryToBr'
import {PaymentAccordion} from '../components/payment/PaymentAccordion'
import {PaymentCard} from './components/paymentCard'
import './styles/indicator.scss'
import {useState} from 'react'

function getSessionName(num: number | any): string {
  if (typeof num === 'number') {
    return num === 1 ? `${num} Sessāo` : `${num} Sessões`
  }
  return ''
}

function calculateTotalPaid(data) {
  const total = data.reduce((accTotal, billing) => {
    if (billing.recurrencePayment) {
      const {amount, installmentsNumber, attemptsSuccessful} = billing.recurrencePayment

      const amountNum = Number(amount) || 0
      const installmentsNum = Number(installmentsNumber) || 0
      const attemptsNum = Number(attemptsSuccessful) || 0

      if (installmentsNum > 0) {
        return accTotal + (amountNum / installmentsNum) * attemptsNum
      }
      return accTotal
    }
    return accTotal + Number(billing.amount || 0)
  }, 0)
  return formatMonetaryToBr(total)
}

function calculateTotalization(data) {
  const total = data.reduce((accTotal, billing) => {
    const amountNum = Number(billing.amount) || 0
    return accTotal + amountNum
  }, 0)

  return formatMonetaryToBr(total)
}

type TPaymentLinkInfo = {
  code: string
  billingId: number | null
  installmentNumber: number | null
  installmentsTotal: number | null
}

export const PaymentPage = () => {
  // payment/:id (encrypt da venda, links antigos) ou p/:code (link curto)
  const {id, code} = useParams()
  const csrf = () => axios.get(process.env.REACT_APP_API_URL + '/sanctum/csrf-cookie')
  const [hasFetched, setHasFetched] = useState(false) // Controle de requisição

  const {
    isLoading,
    data: response,
    error,
  } = useQuery(
    `${QUERIES.PAYMENT_SHOW}-${String(code ?? id)}`,
    async () => {
      await csrf()
      if (code) {
        const {data: sale, link} = await getSaleByPaymentLink(code)
        return {sale, link: link as TPaymentLinkInfo}
      }
      return {sale: await getSaleById(String(id)), link: undefined}
    },
    {
      cacheTime: 0,
      enabled: !hasFetched,
      onSuccess: () => setHasFetched(true),
      onError: (err) => {
        setHasFetched(true)
      },
    }
  )

  if (isLoading) {
    return <Spinner />
  }

  if (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status
      switch (status) {
        case 404:
          return <NotFoundIdShowPage source={code ? 'Link de pagamento' : 'Venda'} />
        default:
          return <HasErrorInShowPage />
      }
    }
    return <HasErrorInShowPage />
  }

  const data = response!.sale
  const link = response!.link
  const {billings, client} = data

  // Link de cobrança avulsa (parcela de recorrência): o status considerado é o da própria cobrança
  const isBillingLink = !!link?.billingId
  const status = isBillingLink ? billings?.[0]?.status : data.status
  const statusPaid = isBillingLink ? ['Pago'] : ['Pago', 'Emitido', 'Assinado', 'Agendamento']
  const statusPending = isBillingLink ? ['Pendente'] : ['Pendente', 'Inadimplente']

  const conditionalPaymentAccordionRender = () => {
    return (
      <div className='accordion' id='kt_accordion_1'>
        <h2 className='m-7 mb-4 text-redlaser'>Escolha uma forma de pagamento</h2>
        {billings
          ?.filter(billing => billing.status !== 'Cancelado')
          .map((billing) => (
            <PaymentAccordion
              key={billing.id}
              billing={billing}
              saleId={code ? undefined : data.encryptId}
              paymentLink={code}
              address={client.addresses}
            />
        ))}
      </div>
    )
  }

  const renderAccordionPaid = () => (
    <div className='m-10 p-8 d-flex flex-column justify-content-center align-items-center'>
      <i className='fa-solid fa-circle-check me-2 text-success' style={{fontSize: 48}}></i>
      <span className='fs-1 text-redlaser fw-bold'>Finalizamos o processo de pagamento!</span>
      <span className='fs-3 text-textlaser text-center w-75'>
        {isBillingLink
          ? 'O pagamento da sua parcela foi confirmado.'
          : 'Assim que confirmamos o mesmo você receberá no seu e-mail o contrato para assinatura digital.'}
      </span>
    </div>
  )

  const renderAccordionOtherStatus = () => (
    <div className='m-10 p-8 d-flex flex-column justify-content-center align-items-center'>
      <i className='fa-solid fa-circle-exclamation me-2 text-danger' style={{fontSize: 48}}></i>
      <span className='fs-1 text-redlaser fw-bold'>
        O processo de pagamento foi encerrado ou expirou!
      </span>
      <span className='fs-3 text-textlaser text-center w-75'>
        Caso precise de ajuda ou queira reiniciar o processo, entre em contato com nossa equipe.
      </span>
    </div>
  )

  return (
    <>
      <div className='conteiner ' style={{backgroundColor: '#D31145', height: '60px'}}>
        <div className='text-center m-4'>
          <img src={toAbsoluteUrl('/media/misc/marcaDrlaserBranca.png')} className='h-30px' />
        </div>

        <div className='d-flex flex-center p-0 p-md-10 '>
          <div
            className='card w-lg-1000px pt-2 pb-5 border border-white'
            style={{borderWidth: '50px'}}
          >
            <div className='card-body p-1 p-md-10'>
              <div className='p-2 ps-8 bg-redlaser welcome-payment'>
                <div className='col-12'>
                  <span className='fs-2 fw-normal text-white'>
                    Olá, <span className='fs-2 fw-bold text-white'>{data?.client?.name}</span>
                  </span>
                </div>
              </div>
              {!statusPending.includes(status) && !statusPaid.includes(status) ? (
                renderAccordionOtherStatus()
              ) : (
                <>
                  <div className='mt-10 px-6 py-4 pb-8' style={{backgroundColor: '#F8EDF1'}}>
                    <div className='mb-4'>
                      <span className='fs-1 fw-bold text-redlaser mb-4'>Serviço</span>
                    </div>

                    {isBillingLink && (
                      <div className='row align-items-center'>
                        <span className='fs-2 text-textlaser'>
                          {link?.installmentNumber && link?.installmentsTotal
                            ? `Parcela ${link.installmentNumber} de ${link.installmentsTotal}`
                            : 'Parcela'}{' '}
                          referente ao seu contrato
                        </span>
                      </div>
                    )}

                    {!isBillingLink && data.transferFromNew && (
                      <>
                        <div className='table-responsive'>
                          <table className='table table-hover table-row-dashed table-row-gray-300 text-gray-800 table-light gy-7 gs-7'>
                            <thead>
                              <tr className='fw-bold fs-6 text-gray-800'>
                                <th>Produto</th>
                                <th>Sessão</th>
                                <th>Realizadas</th>
                                <th>Pendente</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.transferFromNew.additionalData.transferValues.itemsTransferValues.map(
                                (item, index) => (
                                  <tr
                                    key={`transferProducts${index}`}
                                    className='align-items-center'
                                  >
                                    <td>{item.productName}</td>
                                    <td>{item.sessions}</td>
                                    <td>{item.sessionsRealized}</td>
                                    <td>{item.remainingSessions}</td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className='border-top border-danger border-2 my-3' />
                        <div className='row align-items-center justify-content-end'>
                          <span className='fs-2 fw-bold text-redlaser mb-4 text-end'>
                            Contrato Origem - Valor Total:{' '}
                            {formatMask(data.transferFromNew.originSale.price)}
                          </span>
                        </div>
                        <div className='row align-items-center justify-content-end'>
                          <span className='fs-2 fw-bold text-redlaser mb-4 text-end'>
                            Total Pago:{' '}
                            {calculateTotalPaid(data.transferFromNew.originSale.billings)}
                          </span>
                        </div>
                        <div className='row align-items-center justify-content-end'>
                          <span className='fs-2 fw-bold text-redlaser mb-4 text-end'>
                            Total Faltante: {calculateTotalization(billings?.filter(billing => billing.status !== 'Cancelado'))}
                          </span>
                        </div>
                      </>
                    )}

                    {!isBillingLink && !data.transferFromNew && (
                      <>
                        <div className='table-responsive'>
                          <table className='table table-hover table-row-dashed table-row-gray-300 text-gray-800 table-light gy-7 gs-7'>
                            <thead>
                              <tr className='fw-bold fs-6'>
                                <th>Tipo</th>
                                <th>Item</th>
                                <th>N° Sessoes</th>
                                <th>Vr Bruto</th>
                                <th>Desconto Ind</th>
                                <th>Vr Final</th>
                              </tr>
                            </thead>
                            <tbody>
                              {data.items.map((item, index) => (
                                <tr key={`normalProducts${index}`}>
                                  <td>{item.product?.category?.name}</td>
                                  <td>{item.product?.name}</td>
                                  <td>{getSessionName(item.sessions)}</td>
                                  <td>{formatMask(item.price)}</td>
                                  <td>{formatMask(item.discount)}</td>
                                  <td>{formatMask(item.totalPrice)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className='border-top border-danger border-2 my-3' />
                        <div className='row align-items-center justify-content-end'>
                          <span className='fs-2 fw-bold text-redlaser mb-4 text-end'>
                            Total: {formatMask(data.price)}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className='mt-10 px-6 py-4 pb-8' style={{backgroundColor: '#F8EDF1'}}>
                    <div className='mb-4'>
                      <span className='fs-1 fw-bold text-redlaser mb-4'>Forma de Pagamento </span>
                    </div>
                    {billings?.filter(billing => billing.status !== 'Cancelado').map((billing, index) => (
                      <div key={index} className='row'>
                        <PaymentCard billing={billing} />
                        {index < billings.length - 1 && <div className='my-3' />}
                      </div>
                    ))}
                    <div className='border-top border-danger border-2 my-3' />
                    <div className='row align-items-center justify-content-end'>
                      <span className='fs-2 fw-bold text-redlaser mb-4 text-end'>
                        Total: {calculateTotalization(data.billings?.filter(billing => billing.status !== 'Cancelado'))}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className='p-2'>
              {statusPending.includes(status) && conditionalPaymentAccordionRender()}

              {statusPaid.includes(status) && renderAccordionPaid()}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
